"use client";

import React from "react";
import Flashcard from "../shared/Flashcard";
import Navbar from "../shared/Navbar";
import TextAnimation from "../shared/TextAnimation";
import { motion, useTransform, useScroll } from "motion/react";
import ContentAnimation from "../shared/ContentAnimation";
import Image from "next/image";
import blessed from "../../assets/icons/blessed-face.svg";
import happy from "../../assets/icons/happy-face.svg";
import smirk from "../../assets/icons/smirk-face.svg";
import surprised from "../../assets/icons/surprised-face.svg";

const navVariants = {
  hidden: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { delay: 1.5, ease: "easeInOut", duration: 1 },
  },
};

const flashcardVariants = (direction: "left" | "right") => ({
  initial: {
    scale: 1,
    x: direction === "left" ? -600 : 600,
    rotate: direction === "left" ? -5 : 5,
    y: 0,
    opacity: 0,
  },
  hover: {
    scale: 1.05,
    rotate: direction === "left" ? -9 : 9,
    y: -10,
    transition: { duration: 0.3 },
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 1,
      delay: 0.5,
      ease: [0.3, 0, 0.4, 1],
    },
  },
});

const FlashcardStack = ({
  direction,
  cards,
}: {
  direction: "left" | "right";
  cards: Array<{
    question: string;
    hint: string;
    subject: string;
    color: string;
    subjectColor: string;
  }>;
}) => (
  <div
    className={`relative w-full sm:w-[400px] h-[180px] sm:h-[400px] max-sm:hidden flex items-center justify-center sm:block ${
      direction === "left" ? "mb-8" : "mt-8"
    } sm:m-0`}
  >
    {cards.map((card, index) => {
      const positions = {
        left: [
          { className: "left-[5%] sm:top-[-100px] sm:left-[16px] z-10" },
          { className: "left-[35%] sm:top-[-30px] sm:left-[166px] z-20" },
          {
            className: "left-[65%] sm:top-[130px] sm:left-[16px] z-30 sm:z-10",
          },
        ],
        right: [
          { className: "right-[5%] sm:top-[-100px] sm:right-[16px] z-10" },
          { className: "right-[35%] sm:top-[-30px] sm:right-[166px] z-20" },
          {
            className:
              "right-[65%] sm:top-[130px] sm:right-[16px] z-30 sm:z-10",
          },
        ],
      };

      return (
        <motion.div
          key={card.question}
          className={`absolute ${positions[direction][index].className}`}
          variants={flashcardVariants(direction)}
          initial="initial"
          animate="animate"
          whileHover="hover"
        >
          <Flashcard {...card} />
        </motion.div>
      );
    })}
  </div>
);

interface HeroProps {
  scrollYProgress: ReturnType<typeof useScroll>["scrollYProgress"];
}

const Hero = ({ scrollYProgress }: HeroProps) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    console.log(isMobile);
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const scale = useTransform(
    scrollYProgress,
    [0, 1],
    ["100%", isMobile ? "0%" : "98%"]
  );
  const rotate = useTransform(
    scrollYProgress,
    [0, 0.6, 1],
    [0, isMobile ? -3 : -6, 0]
  );
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const leftCards = [
    {
      question: "What is the pH range of acids versus bases?",
      hint: "Lower numbers indicate higher acidity, higher numbers indicate basicity, and 7 is neutral",
      subject: "CHEM-102",
      color: "#DFF2FE",
      subjectColor: "#F0F9FF",
    },
    {
      question: "What is the formula for kinetic energy?",
      hint: "Involves mass and velocity squared.",
      subject: "PHYS-101",
      color: "#DAB2FF",
      subjectColor: "#EDE9FE",
    },
    {
      question: "What's the derivative of sin(x)?",
      hint: "Think about basic trigonometric differentiation.",
      subject: "MATH-101",
      color: "#FF6467",
      subjectColor: "#FFA2A2",
    },
  ];

  const rightCards = [
    {
      question: "What is cognitive load?",
      hint: "Amount of working memory resources used",
      subject: "PSYCH-101",
      color: "#DAB2FF",
      subjectColor: "#EDE9FE",
    },
    {
      question: "What is the ideal gas law equation?",
      hint: "Involves pressure, volume, temperature, and moles.",
      subject: "CHEM-102",
      color: "#FFD230",
      subjectColor: "#FEE685",
    },
    {
      question: "What is metacognition?",
      hint: "Thinking about one's own thinking process",
      subject: "LEARNING-101",
      color: "#7BF1A8",
      subjectColor: "#B9F8CF",
    },
  ];

  return (
    <motion.div
      className="sticky top-0 z-[1] overflow-hidden"
      initial="initial"
      animate="animate"
      variants={{
        initial: {
          y: "110%",
          borderTopLeftRadius: isMobile ? "0%" : "60%",
          borderTopRightRadius: isMobile ? "0%" : "60%",
        },
        animate: {
          y: 0,
          borderTopLeftRadius: "0%",
          borderTopRightRadius: "0%",
        },
      }}
      transition={{
        duration: 1.5,
        ease: [0.87, 0, 0.5, 1],
      }}
    >
      <motion.div
        className={"w-full h-full bg-white"}
        style={{
          scale,
          rotate,
          y,
        }}
      >
        <main className="rounded-t-[80px] overflow-hidden">
          <motion.div variants={navVariants} initial="hidden" animate="animate">
            <Navbar />
          </motion.div>
          <section className="flex flex-col items-center py-8 px-4 sm:px-0 sm:py-0 sm:flex-row sm:justify-evenly sm:gap-20 max-w-screen-2xl 3xl:max-w-screen-4xl mx-auto min-h-screen relative z-[1]">
            <FlashcardStack direction="left" cards={leftCards} />

            <div className="flex flex-col items-center justify-center sm:gap-5 z-[100] max-sm:mt-8">
              <ContentAnimation delay={0.2}>
                <div className="py-2 px-5 bg-slate-900 rounded-[20px] text-white tracking-tight font-semibold max-sm:text-sm max-sm:mb-4">
                  Still cooking 🍳
                </div>
              </ContentAnimation>
              <h1>
                <TextAnimation
                  text="SwiftStudy"
                  className="font-editorial font-extrabold text-[2.5rem] sm:text-[120px] 3xl:text-[9vw] tracking-[-0.06em] z-[100] text-center sm:my-0 3xl:tracking-[-0.07em]"
                  y="100%"
                />
              </h1>
              <ContentAnimation delay={0.4}>
                <p className="text-center sm:text-xl max-sm:mt-4">
                  Obtain an <span className="font-bold">Unfair Advantage*</span>{" "}
                  in Studying
                </p>
              </ContentAnimation>
            </div>

            <div className="relative h-[180px] sm:hidden w-full">
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  top: "65%",
                  left: "54%",
                }}
              >
                <Flashcard
                  question="What is the pH range of acids versus bases?"
                  hint="Lower numbers indicate higher acidity, higher numbers indicate basicity, and 7 is neutral"
                  subject="CHEM-102"
                  color="#DFF2FE"
                  subjectColor="#F0F9FF"
                />
              </div>
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  top: "70%",
                  left: "51%",
                }}
              >
                <Flashcard
                  question="What is the formula for kinetic energy?"
                  hint="Involves mass and velocity squared."
                  subject="PHYS-101"
                  color="#DAB2FF"
                  subjectColor="#EDE9FE"
                />
              </div>
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  top: "75%",
                  left: "48%",
                }}
              >
                <Flashcard
                  question="What is metacognition?"
                  hint="Thinking about one's own thinking process"
                  subject="LEARNING-101"
                  color="#7BF1A8"
                  subjectColor="#B9F8CF"
                />
              </div>
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{
                  top: "80%",
                  left: "45%",
                }}
              >
                <Flashcard
                  question="What is the ideal gas law equation?"
                  hint="Involves pressure, volume, temperature, and moles."
                  subject="CHEM-102"
                  color="#FFD230"
                  subjectColor="#FEE685"
                />
              </div>
            </div>

            {/* Mobile Emoticons */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-40 sm:hidden">
              <motion.div
                initial={{ scale: 0, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
              >
                <Image src={blessed} alt="blessed face" className="w-8 h-8" />
              </motion.div>
              <motion.div
                initial={{ scale: 0, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <Image src={happy} alt="happy face" className="w-8 h-8" />
              </motion.div>
              <motion.div
                initial={{ scale: 0, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.4 }}
              >
                <Image src={smirk} alt="smirk face" className="w-8 h-8" />
              </motion.div>
              <motion.div
                initial={{ scale: 0, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.6 }}
              >
                <Image
                  src={surprised}
                  alt="surprised face"
                  className="w-8 h-8"
                />
              </motion.div>
            </div>

            <FlashcardStack direction="right" cards={rightCards} />
          </section>
        </main>
      </motion.div>
    </motion.div>
  );
};

export default Hero;
